/**
 * Zahnheld drawer motion ("Schnitt").
 *
 * Impact ships a single `Drawer` base class, registered as `x-drawer`. Cart,
 * search, wishlist, navigation, facet, quick-buy, cart-notification and the
 * newsletter popup drawer all extend it, and none of them override the two
 * transition hooks. Patching the base prototype therefore gives every drawer in
 * the theme one shared motion language, which is what issue #413 asks for.
 *
 * Why replace the stock motion at all: Impact's curve is [0.86, 0, 0.07, 1]
 * over 400ms, which holds almost still through its first half (0.2% of the
 * reveal is done at 25% of the runtime, 68% at 50%) and then snaps. It reads as
 * "nothing happened, then the drawer was suddenly there". This replaces it with
 * a decisive edge wipe plus a short staggered settle of the three shadow parts.
 *
 * Opening directions are deliberately left alone: cart opens from the right on
 * every viewport, search from the top below 700px, wishlist and navigation from
 * the bottom. Only the curve, the timing and the choreography change here.
 *
 * Contract that must not break:
 * - both hooks return a promise; the focus trap awaits it via
 *   `checkCanFocusTrap` / `checkCanReturnFocus` (assets/theme.js:1484, :1500)
 * - `prefers-reduced-motion: reduce` and the `reduce_drawer_animation` theme
 *   setting fall back to a plain opacity fade
 * - `open="immediate"` skips straight to the end state
 * - the `show-close-cursor` class is added on show and removed on hide
 *
 * The one non-obvious dependency, worth stating because it is easy to break:
 * theme.css keeps `.drawer { visibility: hidden }` at all times and ships no
 * `[open]` counterpart. A drawer is visible *only* because its show transition
 * leaves `visibility: visible` behind; Motion One did that by committing inline
 * styles. Hence the split in fill modes:
 *
 * - the host animation fills forwards (`both`) in BOTH directions. Opening it
 *   holds `visibility: visible`, closing it holds `opacity: 0`, and closing
 *   keeps its animation alive afterwards on purpose. That surviving `opacity: 0`
 *   is the backstop for descendants that carry an inline `visibility: visible`
 *   (see the navigation panel fix at the bottom of this file).
 * - everything else fills backwards, so no `transform` is left sitting on
 *   `[part=body]`, where it would become the containing block for absolutely
 *   positioned descendants such as `.cart-drawer__note`.
 */
(function () {
  'use strict';

  /* easeOutCubic / easeInCubic. Picked by measuring how the reveal distributes
     across the runtime rather than by name. At 12.5 / 25 / 50 / 75% of the
     duration easeOutCubic has travelled 33 / 58 / 87 / 98% of the distance: it
     departs on the first frame and still moves a visible 11% through its last
     quarter. The two curves either side of it both fail, in opposite ways.
     Impact's stock [0.86, 0, 0.07, 1] sits at 1 / 4 / 68 / 97%, so it stalls and
     then snaps. Anything as steep as [0.16, 1, 0.3, 1] sits at 58 / 83 / 97 /
     100%, so it snaps and then drifts imperceptibly. */
  var EASE_OUT = 'cubic-bezier(0.33, 1, 0.68, 1)';
  var EASE_IN = 'cubic-bezier(0.32, 0, 0.67, 0)';

  /* Distance in px the shadow parts travel along the opening axis. Small on
     purpose: it should reinforce the direction of the wipe, not read as a
     second, competing slide. */
  var SHIFT = 6;

  /* Opening totals 410ms (the parts are the last thing to land). */
  var OPEN = {
    hostFade: 100,
    wipe: 320,
    wipeDelay: 40,
    part: 180,
    partDelay: 150,
    partStagger: 40,
    closeButton: 150,
    closeButtonDelay: 150
  };

  /* Closing mirrors the opening but is tightened to 300ms, so dismissing never
     feels like it is holding the customer up. */
  var CLOSE = {
    part: 90,
    partStagger: 20,
    closeButton: 90,
    wipe: 240,
    wipeDelay: 60,
    hostFade: 100,
    hostFadeDelay: 200
  };

  var REDUCED = 200;

  function prefersReducedMotion() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
    var settings = window.themeVariables && window.themeVariables.settings;
    return Boolean(settings && settings.reduceDrawerAnimation);
  }

  /**
   * Impact's `_getClipPathProperties()` ships an unbalanced paren in its
   * ltr/"right" branch (assets/theme.js:1610), which happens to be the cart
   * drawer's only direction. Chrome currently auto-closes it during var()
   * substitution, but that is lenient parsing we should not depend on.
   *
   * Returns null when Impact hands back anything other than the expected pair,
   * which the callers treat as "fall back to the plain fade" rather than letting
   * a `.map()` on undefined take the whole drawer down.
   *
   * @param {Element} drawer
   * @returns {?string[]} `[closed, open]` clip-path values with balanced parens
   */
  function clipPathKeyframes(drawer) {
    var values = drawer._getClipPathProperties();
    if (!values || values.length < 2) return null;

    return Array.prototype.map.call(values, function (value) {
      var open = (String(value).match(/\(/g) || []).length;
      var close = (String(value).match(/\)/g) || []).length;
      return open > close ? value + new Array(open - close + 1).join(')') : value;
    });
  }

  /**
   * Offset the shadow parts start from, following the physical opening edge.
   * `left` and `right` swap sides under RTL because `_setInitialPosition()`
   * pins the host to the opposite edge there (assets/theme.js:1618).
   *
   * @param {string} openFrom - one of left, right, top, bottom
   * @returns {string} a `translateX`/`translateY` value
   */
  function partOffset(openFrom) {
    var rtl = document.dir === 'rtl';
    switch (openFrom) {
      case 'left':
        return 'translateX(' + (rtl ? SHIFT : -SHIFT) + 'px)';
      case 'right':
        return 'translateX(' + (rtl ? -SHIFT : SHIFT) + 'px)';
      case 'top':
        return 'translateY(' + -SHIFT + 'px)';
      default:
        return 'translateY(' + SHIFT + 'px)';
    }
  }

  /* Empty slots are hidden by `_updateSlotVisibility()`, so skip them rather
     than burning a stagger step on something nobody sees. */
  function visibleParts(content) {
    return Array.prototype.filter.call(content.children, function (el) {
      return !el.hidden;
    });
  }

  /**
   * Settles a run of animations into one promise that never rejects. A
   * cancelled animation rejects its `finished` promise with AbortError, and the
   * focus trap would stall on that.
   *
   * @param {Element} drawer
   * @param {Animation[]} animations
   * @param {boolean} animate - false jumps straight to the end state
   * @param {Function} apply - runs only if no newer transition has started
   * @returns {Promise<void>}
   */
  function settle(drawer, animations, animate, apply) {
    drawer._zhMotion = (drawer._zhMotion || []).concat(animations);

    if (!animate) {
      animations.forEach(function (animation) {
        animation.finish();
      });
    }

    var generation = drawer._zhMotionGeneration;

    return Promise.all(
      animations.map(function (animation) {
        return animation.finished.catch(function () {});
      })
    ).then(function () {
      /* A newer transition may have started while this one was running; it owns
         the class now, so do not let a stale run stomp on it. */
      if (drawer._zhMotionGeneration === generation) apply();
    });
  }

  function beginGeneration(drawer) {
    drawer._zhMotionGeneration = (drawer._zhMotionGeneration || 0) + 1;
  }

  /**
   * Cancels the tracked run, optionally sparing one animation. Closing spares
   * its own host animation, because that is the only thing still holding
   * `opacity: 0`; dropping it would let any descendant carrying an inline
   * `visibility: visible` paint over the page. See the panel fix below.
   *
   * @param {Element} drawer
   * @param {Animation} [keep]
   */
  function cancelPending(drawer, keep) {
    var kept = [];
    (drawer._zhMotion || []).forEach(function (animation) {
      if (keep && animation === keep) {
        kept.push(animation);
        return;
      }
      try {
        animation.cancel();
      } catch (error) {
        /* Already finished or detached. */
      }
    });
    drawer._zhMotion = kept;
  }

  /* The plain opacity fade, used for reduced motion and as the fallback when the
     shadow DOM or Impact's clip-path helper is not shaped the way this module
     expects. Falling back here still satisfies the visibility contract, which is
     the part that must never fail. */
  function fadeIn(drawer) {
    return drawer.animate(
      { opacity: [0, 1], visibility: ['hidden', 'visible'] },
      { duration: REDUCED, fill: 'both' }
    );
  }

  function fadeOut(drawer) {
    return drawer.animate([{ opacity: 0, visibility: 'hidden' }], {
      duration: REDUCED,
      fill: 'both'
    });
  }

  /**
   * @returns {Animation[]} the opening run, host animation first
   */
  function showAnimations(drawer, content, closeButton) {
    var clip = content && clipPathKeyframes(drawer);
    if (prefersReducedMotion() || !clip) return [fadeIn(drawer)];

    var offset = partOffset(drawer.openFrom);
    var animations = [
      /* `fill: both` is load-bearing here, not a style choice: it is what keeps
         `visibility: visible` on the host after the animation ends. */
      drawer.animate(
        { opacity: [0, 1], visibility: ['hidden', 'visible'] },
        { duration: OPEN.hostFade, easing: 'linear', fill: 'both' }
      ),
      content.animate(
        { clipPath: clip },
        { duration: OPEN.wipe, delay: OPEN.wipeDelay, easing: EASE_OUT, fill: 'backwards' }
      )
    ];

    /* header, body, footer settle in behind the wipe. `fill: backwards` holds
       the from-state through the delay (otherwise they flash at full opacity
       first) and drops the transform once done, so no stray containing block is
       left behind on a scrolling part. */
    visibleParts(content).forEach(function (part, index) {
      animations.push(
        part.animate(
          { opacity: [0, 1], transform: [offset, 'translate(0)'] },
          {
            duration: OPEN.part,
            delay: OPEN.partDelay + index * OPEN.partStagger,
            easing: EASE_OUT,
            fill: 'backwards'
          }
        )
      );
    });

    if (closeButton) {
      animations.push(
        closeButton.animate(
          { opacity: [0, 1] },
          {
            duration: OPEN.closeButton,
            delay: OPEN.closeButtonDelay,
            easing: 'linear',
            fill: 'backwards'
          }
        )
      );
    }

    return animations;
  }

  /**
   * Single-keyframe lists take their start value from the effect stack
   * underneath, so closing mid-open picks up wherever the opening run currently
   * is instead of snapping back to fully open. That is also why the caller must
   * not cancel the opening run before this builds.
   *
   * @returns {{animations: Animation[], host: Animation}}
   */
  function hideAnimations(drawer, content, closeButton) {
    var clip = content && clipPathKeyframes(drawer);
    if (prefersReducedMotion() || !clip) {
      var reducedHost = fadeOut(drawer);
      return { animations: [reducedHost], host: reducedHost };
    }

    var offset = partOffset(drawer.openFrom);
    var animations = [];

    /* Mirrored order: the part that settled last leaves first. `slice()` because
       `reverse()` is in place, and the list must not be reversed for the caller. */
    visibleParts(content)
      .slice()
      .reverse()
      .forEach(function (part, index) {
        animations.push(
          part.animate([{ opacity: 0, transform: offset }], {
            duration: CLOSE.part,
            delay: index * CLOSE.partStagger,
            easing: EASE_IN,
            fill: 'both'
          })
        );
      });

    if (closeButton) {
      animations.push(
        closeButton.animate([{ opacity: 0 }], {
          duration: CLOSE.closeButton,
          easing: 'linear',
          fill: 'both'
        })
      );
    }

    animations.push(
      content.animate([{ clipPath: clip[0] }], {
        duration: CLOSE.wipe,
        delay: CLOSE.wipeDelay,
        easing: EASE_IN,
        fill: 'both'
      })
    );

    var host = drawer.animate([{ opacity: 0, visibility: 'hidden' }], {
      duration: CLOSE.hostFade,
      delay: CLOSE.hostFadeDelay,
      easing: 'linear',
      fill: 'both'
    });
    animations.push(host);

    return { animations: animations, host: host };
  }

  function showTransition(animate) {
    if (animate === undefined) animate = true;

    this._setInitialPosition();

    /* Clear the closing run first: its keyframes use `fill: both`, so their end
       state would otherwise still be pinned on the parts. */
    cancelPending(this);
    beginGeneration(this);

    var drawer = this;
    var content = this.shadowRoot.querySelector('[part="content"]');
    var closeButton = this.shadowRoot.querySelector('[part="outside-close-button"]');

    return settle(this, showAnimations(this, content, closeButton), animate, function () {
      drawer.classList.add('show-close-cursor');
    });
  }

  function hideTransition() {
    beginGeneration(this);

    var drawer = this;
    var content = this.shadowRoot.querySelector('[part="content"]');
    var closeButton = this.shadowRoot.querySelector('[part="outside-close-button"]');
    var run = hideAnimations(this, content, closeButton);

    return settle(this, run.animations, true, function () {
      drawer.classList.remove('show-close-cursor');
      /* Drop everything except this run's host animation. Dropping the part
         animations keeps a `transform` off `[part=body]`; keeping the host one
         holds `opacity: 0; visibility: hidden`. Sparing exactly this animation
         rather than "any animation on the host" also retires the opening run, so
         a closed drawer never carries two filled animations on the same
         properties. `settle` only reaches this when no newer transition has
         started. */
      cancelPending(drawer, run.host);
    });
  }

  function patch(DrawerClass) {
    if (!DrawerClass || DrawerClass.prototype._zhDrawerMotion) return;

    /* Both hooks lean on Impact internals. If a future Impact release renames or
       drops one of them, leave the stock transitions alone rather than replacing
       them with something that throws: `attributeChangedCallback` does not catch
       (assets/theme.js:1484), so a throwing hook would leave the focus trap
       waiting on a promise that never arrives and the drawer unusable. Losing
       the restyled motion is by far the cheaper failure. */
    if (
      typeof DrawerClass.prototype._getClipPathProperties !== 'function' ||
      typeof DrawerClass.prototype._setInitialPosition !== 'function'
    ) {
      return;
    }

    DrawerClass.prototype._showTransition = showTransition;
    DrawerClass.prototype._hideTransition = hideTransition;
    DrawerClass.prototype._zhDrawerMotion = true;
  }

  /**
   * Fixes the menu list staying painted over the page after the drawer closes.
   *
   * `NavigationDrawer.reinitializeDrawer()` runs on `dialog:after-hide` and
   * writes an inline `visibility: visible` onto `.panel:first-child`
   * (assets/theme.js:4613). An inline declaration outranks the
   * `visibility: hidden` the panel would otherwise inherit from the host, so
   * from the first close onwards the whole menu list is paintable again and the
   * only thing still hiding it is the host's `opacity: 0`. Lose that opacity for
   * any reason and the numbered menu items draw straight over the page.
   *
   * Removing the declaration rather than overriding it lets the panel inherit
   * again, which is right in both states: hidden while the drawer is closed,
   * visible while it is open. It is also why this cannot be a CSS rule keyed on
   * `:not([open])`: the attribute is dropped before the close transition runs,
   * so such a rule would blank the list at the start of the closing animation.
   *
   * Only the `visible` that this method just wrote is removed. Sibling panels
   * keep their inline `hidden`, which they need in order to stay collapsed while
   * the drawer is open.
   */
  function patchNavigationDrawer(NavigationDrawerClass) {
    if (!NavigationDrawerClass || NavigationDrawerClass.prototype._zhPanelVisibilityFix) return;
    var original = NavigationDrawerClass.prototype.reinitializeDrawer;
    if (typeof original !== 'function') return;

    NavigationDrawerClass.prototype.reinitializeDrawer = function () {
      original.call(this);
      var firstPanel = this.querySelector('.panel:first-child');
      if (firstPanel && firstPanel.style.visibility === 'visible') {
        firstPanel.style.removeProperty('visibility');
      }
    };
    NavigationDrawerClass.prototype._zhPanelVisibilityFix = true;
  }

  function whenDefined(name, apply) {
    var defined = window.customElements.get(name);
    if (defined) {
      apply(defined);
      return;
    }
    /* Older implementations resolve `whenDefined` with `undefined` instead of
       the constructor, which would skip the patch without a sound. Look the
       class up again rather than trusting the resolution value. */
    window.customElements.whenDefined(name).then(function () {
      apply(window.customElements.get(name));
    });
  }

  whenDefined('x-drawer', patch);
  whenDefined('navigation-drawer', patchNavigationDrawer);
})();
